// [NEW] EventListScreen Component - Browse semua event aktif
import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, limit, Timestamp } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { HiOutlineCalendar, HiOutlineLocationMarker, HiOutlineClock, HiOutlineTicket } from 'react-icons/hi';

const EventListScreen = ({ user, onBack, onEventSelect, onOpenMyTickets }) => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState('upcoming'); // 'upcoming' | 'all'
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchEvents = async () => {
            setIsLoading(true);
            const eventsRef = collection(db, "artifacts", appId, "public", "data", "events");

            // Query active events with limit
            const q = query(
                eventsRef,
                where("status", "==", "active"),
                orderBy("date", "asc"),
                limit(20) // Limit to reducing reads
            );

            try {
                const snapshot = await getDocs(q);
                const eventData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setEvents(eventData);
            } catch (error) {
                console.error("Error fetching events:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEvents();
    }, []);

    // Filter events
    const filteredEvents = events.filter(event => {
        // Search filter
        if (searchQuery && !event.title?.toLowerCase().includes(searchQuery.toLowerCase())) {
            return false;
        }

        // Time filter
        if (filter === 'upcoming') {
            const eventDate = event.date?.toDate ? event.date.toDate() : new Date(event.date);
            return eventDate > Date.now();
        }

        return true;
    });

    // Get price display
    const getPriceDisplay = (event) => {
        if (event.ticketCategories && event.ticketCategories.length > 0) {
            const fixedPrices = event.ticketCategories
                .filter(c => c.pricingType === 'fixed' && c.enabled)
                .map(c => c.price);

            if (fixedPrices.length === 0) {
                // Has free or pay-as-you-wish only
                const hasFree = event.ticketCategories.some(c => c.pricingType === 'free');
                if (hasFree) return 'Gratis';
                return 'Bayar Sesukamu';
            }

            const min = Math.min(...fixedPrices);
            const max = Math.max(...fixedPrices);

            if (min === max) {
                return `Rp ${min.toLocaleString('id-ID')}`;
            }
            return `Rp ${min.toLocaleString('id-ID')} - ${max.toLocaleString('id-ID')}`;
        }

        // Legacy price
        return event.price > 0 ? `Rp ${event.price.toLocaleString('id-ID')}` : 'Gratis';
    };

    if (isLoading) {
        return (
            <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
                <div className="flex items-center mb-6">
                    <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-200">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                    </button>
                    <h1 className="text-2xl font-bold text-green-900">Event</h1>
                </div>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen pb-24">
            {/* Header */}
            <div className="flex items-center mb-6">
                <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-200">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                </button>
                <h1 className="text-2xl font-bold text-green-900 flex-1">Event</h1>
                <button
                    onClick={onOpenMyTickets}
                    className="ml-auto px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-semibold hover:bg-green-200 transition flex items-center"
                >
                    <HiOutlineTicket className="w-5 h-5 mr-1" />
                    Tiket Saya
                </button>
            </div>

            {/* Search & Filter */}
            <div className="mb-6 space-y-3">
                <input
                    type="text"
                    placeholder="Cari event..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                />

                <div className="flex gap-2">
                    <button
                        onClick={() => setFilter('upcoming')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${filter === 'upcoming'
                            ? 'bg-green-700 text-white'
                            : 'bg-white text-gray-600 border'
                            }`}
                    >
                        Akan Datang
                    </button>
                    <button
                        onClick={() => setFilter('all')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${filter === 'all'
                            ? 'bg-green-700 text-white'
                            : 'bg-white text-gray-600 border'
                            }`}
                    >
                        Semua Event
                    </button>
                </div>
            </div>

            {/* Events List */}
            {filteredEvents.length === 0 ? (
                <div className="text-center py-12">
                    <HiOutlineTicket className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Belum ada event tersedia</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredEvents.map(event => (
                        <div
                            key={event.id}
                            onClick={() => onEventSelect && onEventSelect(event)}
                            className="bg-white rounded-2xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition"
                        >
                            {/* Event Image */}
                            {event.imageUrl && (
                                <img
                                    src={event.imageUrl}
                                    alt={event.title}
                                    className="w-full h-40 object-cover"
                                />
                            )}

                            <div className="p-4">
                                <h3 className="text-lg font-bold text-green-900 mb-2">{event.title}</h3>

                                <div className="space-y-1 text-sm text-gray-600 mb-3">
                                    <div className="flex items-center">
                                        <HiOutlineCalendar className="w-4 h-4 mr-2 text-green-700" />
                                        <span>
                                            {event.date?.toDate?.()?.toLocaleDateString('id-ID', {
                                                weekday: 'long',
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric'
                                            })}
                                        </span>
                                    </div>

                                    <div className="flex items-center">
                                        <HiOutlineClock className="w-4 h-4 mr-2 text-green-700" />
                                        <span>
                                            {event.date?.toDate?.()?.toLocaleTimeString('id-ID', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })} WIB
                                        </span>
                                    </div>

                                    {event.location && (
                                        <div className="flex items-center">
                                            <HiOutlineLocationMarker className="w-4 h-4 mr-2 text-green-700" />
                                            <span className="truncate">{event.location}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-between items-center mt-3">
                                    <span className="text-green-700 font-bold text-lg">
                                        {getPriceDisplay(event)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {event.ticketCategories?.length > 0 ?
                                            `${event.ticketCategories.length} kategori` :
                                            'Single ticket'
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Call to Action */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100 text-center">
                <p className="text-blue-900 font-bold mb-1">Mau bikin event di kampusmu?</p>
                <p className="text-sm text-blue-700 mb-3">Hubungi admin Semesta untuk info lebih lanjut</p>
                <button
                    onClick={() => window.open('https://wa.me/6281229512129', '_blank')}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                >
                    Hubungi Admin
                </button>
            </div>
        </div>
    );
};

export default EventListScreen;
